import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, RotateCcw, X, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

function friendlySize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const STATUS_LABEL = {
  queued:     'Queued',
  processing: 'Processing...',
  done:       'Done',
  failed:     'Failed',
  skipped:    'Skipped',
};

export default function UploadQueueItem({ item, colors, onRetry, onSkip }) {
  const { id, name, size, preview, status, error, receiptId, isPdf, imageWarn } = item;
  const tone = {
    queued:     { bg: colors.surface,  fg: colors.textMuted, border: colors.border },
    processing: { bg: colors.accentSoft, fg: colors.accent, border: colors.accent },
    done:       { bg: '#dcfce7',       fg: '#166534', border: '#86efac' },
    failed:     { bg: '#fee2e2',       fg: '#991b1b', border: '#fca5a5' },
    skipped:    { bg: colors.surface,  fg: colors.textMuted, border: colors.border },
  }[status] || { bg: colors.surface, fg: colors.textMuted, border: colors.border };

  const StatusIcon = {
    queued:     null,
    processing: Loader2,
    done:       CheckCircle2,
    failed:     AlertCircle,
    skipped:    null,
  }[status];

  return (
    <div
      data-testid={`upload-row-${id}`}
      className="flex items-center gap-3 px-3 py-2 rounded-lg border"
      style={{ borderColor: colors.border, backgroundColor: colors.surface, minHeight: 48 }}
    >
      {/* Thumbnail */}
      <div
        className="shrink-0 w-10 h-10 rounded-md overflow-hidden flex items-center justify-center"
        style={{ backgroundColor: colors.surfaceAlt || '#f1f5f9', border: `1px solid ${colors.border}` }}
      >
        {preview ? (
          <img src={preview} alt="" className="w-full h-full object-cover" />
        ) : isPdf ? (
          <FileText size={16} style={{ color: '#dc2626' }} />
        ) : (
          <FileText size={16} style={{ color: colors.textMuted }} />
        )}
      </div>

      {/* Filename + size + error */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="truncate text-sm font-medium" style={{ color: colors.text }} title={name}>
            {name}
          </span>
          <span className="shrink-0 text-xs tabular-nums" style={{ color: colors.textMuted }}>
            {friendlySize(size)}
          </span>
        </div>
        {error && status === 'failed' ? (
          <div className="truncate text-xs mt-0.5" style={{ color: '#991b1b' }} title={error}>
            {error}
          </div>
        ) : (status === 'done' && imageWarn) ? (
          <div className="truncate text-xs mt-0.5" style={{ color: '#92400e' }} title={imageWarn}>
            {imageWarn}
          </div>
        ) : isPdf ? (
          <div className="truncate text-xs mt-0.5" style={{ color: colors.textMuted }}>
            PDF · first page only
          </div>
        ) : null}
      </div>

      {/* Status chip */}
      <div
        className="shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold"
        style={{ backgroundColor: tone.bg, borderColor: tone.border, color: tone.fg }}
      >
        {StatusIcon ? (
          <StatusIcon
            size={12}
            className={status === 'processing' ? 'animate-spin' : ''}
            style={{ color: tone.fg }}
          />
        ) : null}
        {status === 'done' && receiptId ? (
          <Link
            to={`/app/receipts/${receiptId}`}
            className="hover:underline"
            data-testid={`upload-row-link-${id}`}
          >
            {STATUS_LABEL[status]}
          </Link>
        ) : (
          <span>{STATUS_LABEL[status] || status}</span>
        )}
      </div>

      {/* Actions */}
      {status === 'failed' ? (
        <div className="shrink-0 flex items-center gap-1">
          <button
            data-testid={`upload-retry-${id}`}
            onClick={() => onRetry?.(id)}
            className="p-1 rounded hover:opacity-70"
            style={{ color: colors.text }}
            aria-label="Retry upload"
            title="Retry"
          >
            <RotateCcw size={14} />
          </button>
          <button
            data-testid={`upload-skip-${id}`}
            onClick={() => onSkip?.(id)}
            className="p-1 rounded hover:opacity-70"
            style={{ color: colors.textMuted }}
            aria-label="Skip file"
            title="Skip"
          >
            <X size={14} />
          </button>
        </div>
      ) : null}
    </div>
  );
}
